import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { useOrgInviteInfo, useAcceptOrgInvite } from '../../features/org-members/api';
import { apiErrorMessage } from '../../utils/errors';

export default function OrgInvitePage() {
  const { token } = useParams();
  const nav = useNavigate();

  /*
   * The second argument is required and was missing while this file was .jsx:
   * `enabled` came through as undefined, so the hook's `!!token && enabled`
   * guard was always falsy and the invite was never fetched — leaving the page
   * with no org name, no email to match against, and Accept permanently
   * unavailable. The hook still refuses to run on an empty token.
   */
  const { data, isLoading, isError, refetch } = useOrgInviteInfo(token ?? '', true);

  const [userEmail, setUserEmail] = useState('');
  const [isAuthed, setIsAuthed] = useState(false);

  const auth = getAuth();
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setIsAuthed(!!u);
      setUserEmail(u?.email || '');
    });
    return () => unsub();
  }, [auth]);

  const accept = useAcceptOrgInvite(token ?? '');

  const invitedEmail = (data?.email || '').toLowerCase();
  const emailMatches = invitedEmail && userEmail.toLowerCase() === invitedEmail;

  const canAccept = isAuthed && emailMatches;

  const onAccept = async () => {
    if (!canAccept) return;
    try {
      await accept.mutateAsync();
      toast.success('Invite accepted!');
      nav('/'); // or "/orders" or wherever org users land
    } catch (err) {
      console.error(err);
      toast.error(apiErrorMessage(err, 'Failed to accept invite'));
    }
  };

  if (isLoading) {
    return (
      <div className="card">
        <div className="skeleton" style={{ height: 160 }} />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="card">
        <h2 className="card__title">Invite link</h2>
        <p className="muted">This invite link is invalid or expired.</p>
      </div>
    );
  }

  const roleLabel = String(data.role || '')
    .replace('ORG_', '')
    .toLowerCase()
    .replace(/^\w/, (c) => c.toUpperCase());

  return (
    <div className="card" style={{ maxWidth: 720, margin: '0 auto' }}>
      <h2 className="card__title">You’re invited to {data.orgName}</h2>
      <p className="card__subtitle">
        Invite for <strong>{data.email}</strong> • Role: <strong>{roleLabel}</strong>
      </p>

      {/* Auth status */}
      {!isAuthed || !emailMatches ? (
        <div
          className="card"
          style={{
            marginTop: 12,
            background: '#fff7ed',
            border: '1px solid #fed7aa',
          }}
        >
          <p style={{ margin: 0 }}>
            Please sign in{' '}
            {invitedEmail ? (
              <>
                as <strong>{invitedEmail}</strong>
              </>
            ) : (
              ''
            )}{' '}
            to continue.
          </p>

          <div className="actions" style={{ marginTop: 8 }}>
            <Link
              className="btn btn--primary"
              to={`/login?redirect=${encodeURIComponent(window.location.pathname)}`}
            >
              Sign in
            </Link>
            <Link
              className="btn btn--ghost"
              to={`/signup?redirect=${encodeURIComponent(window.location.pathname)}`}
            >
              Create account
            </Link>
          </div>

          {isAuthed && !emailMatches && (
            <div className="muted" style={{ marginTop: 8 }}>
              You are signed in as <strong>{userEmail}</strong>. Please switch accounts.
            </div>
          )}
        </div>
      ) : (
        <div
          className="card"
          style={{
            marginTop: 12,
            background: '#ecfeff',
            border: '1px solid #a5f3fc',
          }}
        >
          <p style={{ margin: 0 }}>
            Signed in as <strong>{userEmail}</strong>.
          </p>
        </div>
      )}

      {/* Accept */}
      <div className="actions" style={{ marginTop: 16 }}>
        <button
          className="btn btn--primary"
          disabled={!canAccept || accept.isPending}
          onClick={onAccept}
        >
          {accept.isPending ? 'Accepting…' : 'Accept invite'}
        </button>

        <button className="btn btn--ghost" onClick={() => refetch()} disabled={accept.isPending}>
          Refresh
        </button>
      </div>

      <div className="muted" style={{ marginTop: 10 }}>
        After accepting, your account will be linked to this organization.
      </div>
    </div>
  );
}
